import { Coordinate } from "client/Misc/commonTypes";
import { EMPTY_ARRAY } from "client/Misc/utils";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import {
	EditorState,
	Plugin,
	PluginKey,
	PluginView,
	Transaction,
	Command
} from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import {
	AttachmentItem,
	isUploadedInlineAttachment,
	UploadedInlineAttachment
} from "../../AttachmentManager/common";
import { hasNodes } from "client/Components/RichTextEditor/schema";
import { Schema } from "prosemirror-model";
import {
	MailMessageLocationModel,
	MailMessageLocationQueryModel
} from "client/Api/Email/common";
import { convertToMailMessageLocationQueryModel } from "client/Features/Email/common";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";

export type EmailComposerPluginState = {
	attachments: readonly AttachmentItem[];
	draftLocation: MailMessageLocationModel | null;
};

const inlineImageSchema = "cid:" as const;
const emailComposerImagePluginKey = new PluginKey<EmailComposerPluginState>("emailComposerImagePlugin");
const emailComposerImagePluginAttachmentKey = new PluginKey<EmailComposerPluginState>("emailComposerImagePluginAttachment");
const emailComposerImagePluginDraftLocationKey = new PluginKey<EmailComposerPluginState>("emailComposerImagePluginDraftLocation");

export function emailComposerImagePlugin(
	onImageContextMenu: (
		target: HTMLImageElement,
		targetNodePosition: number,
		position: Coordinate
	) => void
): Plugin<EmailComposerPluginState> {
	return new Plugin<EmailComposerPluginState>({
		view(viewState: EditorView) {
			return new EmailComposerImagePluginView(viewState);
		},
		state: {
			init(): EmailComposerPluginState {
				return {
					attachments: EMPTY_ARRAY,
					draftLocation: null
				};
			},
			apply(
				tr: Transaction,
				value: EmailComposerPluginState
			): EmailComposerPluginState {
				const attachments = tr.getMeta(
					emailComposerImagePluginAttachmentKey
				) as (AttachmentItem[] | undefined);

				const draftLocation = tr.getMeta(
					emailComposerImagePluginDraftLocationKey
				) as (MailMessageLocationModel | undefined);

				if (!attachments && !draftLocation) {
					return value;
				}

				return {
					...value,
					attachments: attachments ?? value.attachments,
					draftLocation: draftLocation ?? value.draftLocation
				};
			},
		},
		props: {
			handleDOMEvents: {
				"contextmenu": (view: EditorView, event: MouseEvent): boolean => {
					if (event.target instanceof HTMLImageElement) {
						event.preventDefault();
						const targetNodePosition: number = view.posAtDOM(event.target, 0);
						onImageContextMenu(
							event.target,
							targetNodePosition,
							{
								x: event.clientX,
								y: event.clientY
							}
						);
						return false;
					}

					return true;
				}
			}
		},
		key: emailComposerImagePluginKey
	});
}

class EmailComposerImagePluginView implements PluginView {
	private readonly _view: EditorView;

	constructor(
		view: EditorView
	) {
		this._view = view;
	}

	public update(
		view: EditorView,
		_previousState: EditorState
	): void {
		this.processInlineImages(view);
	}

	private processInlineImages(view: EditorView): void {
		const pluginState: EmailComposerPluginState | nil = emailComposerImagePluginKey
			.getState(view.state);

		if (!pluginState) {
			return;
		}

		const draftLocation: MailMessageLocationModel | null = pluginState.draftLocation;

		if (!draftLocation) {
			return;
		}

		const location: MailMessageLocationQueryModel = convertToMailMessageLocationQueryModel(draftLocation);

		const loadedInlineAttachments: UploadedInlineAttachment[] = pluginState.attachments
			.filter(isUploadedInlineAttachment);

		if (loadedInlineAttachments.length === 0) {
			return;
		}

		const cidImageElements = view.dom
			.querySelectorAll<HTMLImageElement>(`img[src^="${inlineImageSchema}"]`);

		cidImageElements.forEach(
			(cidImage: HTMLImageElement): void => {
				const contentId: string = cidImage.src
					.substring(inlineImageSchema.length);

				const attachment: UploadedInlineAttachment | undefined = loadedInlineAttachments
					.find(a => a.contentId === contentId);

				if (attachment) {
					const parameters: string = urlEncodeObject(
						{
							smtpAddress: location.smtpAddress,
							messageId: location.messageId,
							attachmentId: attachment.exchangeId
						},
						true
					);

					cidImage.src = createUrlRelativeToEnvironment(`/Email/DownloadExchangeAttachment?${parameters}`);

					cidImage.dataset["cid"] = contentId;
				}
			}
		);
	}

	private restoreInlineImages(view: EditorView): void {
		const cidImageElements = view.dom
			.querySelectorAll<HTMLImageElement>(`img[data-cid]`);

			cidImageElements.forEach(
				(cidImage: HTMLImageElement): void => {
					cidImage.src = `${inlineImageSchema}${cidImage.dataset["cid"]}`;
				}
			);
	}

	public destroy(): void {
		this.restoreInlineImages(this._view);
	}
}

export function insertInlineImage(contentId: string): Command {
	return (state, dispatch): boolean => {
		const schema: Schema<never, never> = state.schema;

		if (!hasNodes(schema, ["image"])) {
			return false;
		}

		if (dispatch) {
			const tr: Transaction = state.tr;

			const {
				to
			} = state.selection;

			tr.insert(
				to,
				[
					schema.nodes
						.image
						.create(
							{
								src: `${inlineImageSchema}${contentId}`
							}
						)
				]
			);

			tr.scrollIntoView();
			dispatch(tr);
		}
		return true;
	};
}

export function setAttachments(
	attachments: readonly AttachmentItem[]
): Command {
	return (state, dispatch): boolean => {
		if (dispatch) {
			const tr: Transaction = state.tr;

			tr.setMeta(emailComposerImagePluginAttachmentKey, attachments);
			dispatch(tr);
		}

		return true;
	};
}

export function setDraftLocation(
	draftLocation: MailMessageLocationModel
): Command {
	return (state, dispatch): boolean => {
		if (dispatch) {
			const tr: Transaction = state.tr;

			tr.setMeta(emailComposerImagePluginDraftLocationKey, draftLocation);
			dispatch(tr);
		}

		return true;
	};
}
